import dayjs, { Dayjs } from "dayjs"
import React, { useState } from "react"
import { useTranslation } from "react-i18next"
import { useRecoilState } from "recoil"
import CheckBoxGroupField from "src/components/atom/CheckBoxGroupField"
import InputCompanyField from "src/components/atom/InputCompanyField"
import InputDateField from "src/components/atom/InputDateField"
import InputNameField from "src/components/atom/InputNameField"
import InputTextField from "src/components/atom/InputTextField"
import SelectField from "src/components/atom/SelectField"
import { OutlinedButton } from "src/components/buttons/OutlinedButton"
import { useLoginContext } from "src/hooks/useLoginContext"
import { GENDER, PROVINCES, RESPONSE_CODE, SELECT_OTHER, SNACKBAR_TYPES } from "src/modules/const"
import { getCountriesForSelect } from "src/modules/helper"
import { validateField, validateFields } from "src/modules/validation"
import { Member, ProfileService } from "src/openapi"
import { AddressService } from "src/openapi_address"
import { ShowProgress } from "src/states/showProgress"
import { ShowToast } from "src/states/showToast"
import { Box, Button } from "src/UILibrary"
import { ConfirmModal } from "src/UILibrary/ConfirmModal"
import * as yup from "yup"

type Props = {
  item: Member
}

export const UserDetail = ({ item }: Props) => {
  const { t } = useTranslation("translation", { keyPrefix: "mypage.user" })

  const [, setShowToast] = useRecoilState(ShowToast)
  const [, setShowProgress] = useRecoilState(ShowProgress)

  const {
    areas,
    startupStages,
    startupPlans,
    routes,
    companyTypes,
    accessToken,
    checkLoginStatus,
  } = useLoginContext()

  const [firstName, setFirstName] = useState<string>(item.firstName as string)
  const [lastName, setLastName] = useState<string>(item.lastName as string)
  const [firstNameJP, setFirstNameJP] = useState<string>(item.firstNameKana as string)
  const [lastNameJP, setLastNameJP] = useState<string>(item.lastNameKana as string)
  const [birthday, setBirthday] = useState<Dayjs | null>(dayjs(item.birthday as string))
  const [gender, setGender] = useState<string>(item.gender as string)
  const [nation, setNation] = useState<string>(item.country as string)
  const [areaId, setAreaId] = useState<number>(item.areaId as number)
  const [phoneNumber, setPhoneNumber] = useState<string>(item.phoneNumber as string)
  const [postCode, setPostCode] = useState<string>(item.zipCode as string)
  const [province, setProvince] = useState<string>(item.province as string)
  const [city, setCity] = useState<string>(item.city as string)
  const [street, setStreet] = useState<string>(item.streetNo as string)
  const [building, setBuilding] = useState<string>(item.building as string)
  const [nameValidationMessage, setNameValidationMessage] = useState<string>("")
  const [birthdayValidationMessage, setBirthdayValidationMessage] = useState<string>("")
  const [phoneNumberValidationMessage, setPhoneNumberValidationMessage] = useState<string>("")
  const [postCodeValidationMessage, setPostCodeValidationMessage] = useState<string>("")
  const [cityValidationMessage, setCityValidationMessage] = useState<string>("")

  const [magazineRegister, setMagazinRegister] = useState<boolean>(
    item.isMagazinRegister as boolean
  )
  const [startStageID, setStartStageID] = useState<number>(item.startupStageId as number)
  const [startPlanID, setStartPlanID] = useState<number>(item.startupPlanId as number)
  const [magazinValidationMessage, setMagazinValidationMessage] = useState<string>("")
  const [startStageIDValidationMessage, setStartStageIDValidationMessage] = useState<string>("")
  const [startPlanIDValidationMessage, setStartPlanIDValidationMessage] = useState<string>("")

  const [companyName, setCompanyName] = useState<string>(item.startupName as string)
  const [companyNameJP, setCompanyNameJP] = useState<string>(item.startupName as string)
  const [startDate, setStartDate] = useState<Dayjs | null>(
    item.startupOpenDate?.startsWith("0001-01-01") ? null : dayjs(item.startupOpenDate as string)
  )
  const [siteURL, setSiteURL] = useState<string>(item.startupUrl as string)
  const [route, setRoute] = useState<number>(item.routeToKnowId as number)
  const [routeComment, setRouteComment] = useState<string>(item.routeComment as string)
  const [companyType, setCompanyType] = useState<number>(item.startupBusinessTypeId as number)
  const [startDateValidationMessage, setStartDateValidationMessage] = useState<string>("")
  const [siteURLValidationMessage, setSiteURLValidationMessage] = useState<string>("")
  const [routeValidationMessage, setRouteValidationMessage] = useState<string>("")
  const [companyTypeValidationMessage, setCompanyTypeValidationMessage] = useState<string>("")

  const [lounge, setLounge] = useState<boolean>(item.isLoungeMember as boolean)
  const [loungeValidationMessage, setLoungeValidationMessage] = useState<string>("")

  const [showConfirm, setShowConfirm] = useState<boolean>(false)

  const handlePostCodeInput = () => {
    if (!postCode) {
      return
    }

    AddressService.autoCompleteAddressByZipCode(postCode).then((res) => {
      if (res.prefecture) {
        setProvince(res.prefecture as string)
      }

      if (res.city) {
        let city = res.city as string
        if (res.townCandidates && res.townCandidates.length >= 1) {
          city = `${city}${res.townCandidates[0]}`
        }
        setCity(city)
      }
    })
  }

  const validate = async () => {
    let validation = true

    try {
      await validateFields(
        yup.object().shape({
          firstName: yup.string().required(t("please_input_first_name")),
          lastName: yup.string().required(t("please_input_last_name")),
          firstNameJP: yup
            .string()
            .required(t("please_input_first_name_jp"))
            .matches(/^[ァ-ヶｱ-ﾝﾞﾟ一-]*$/, t("please_input_jp_string")),
          lastNameJP: yup
            .string()
            .required(t("please_input_last_name_jp"))
            .matches(/^[ァ-ヶｱ-ﾝﾞﾟ一-]*$/, t("please_input_jp_string")),
        }),
        ["firstName", "lastName", "firstNameJP", "lastNameJP"],
        [firstName, lastName, firstNameJP, lastNameJP]
      )
      validation = validation && true
      setNameValidationMessage("")
    } catch (err: any) {
      setNameValidationMessage(err.message)
      validation = validation && false
    }

    try {
      await validateField(
        yup.object().shape({
          birthday: yup
            .string()
            .required(t("please_input_birthday"))
            .matches(/^(\d{4})\-(\d{1,2})\-(\d{1,2})$/, t("please_input_correct_birthday")),
        }),
        "birthday",
        birthday?.format("YYYY-MM-DD")
      )
      setBirthdayValidationMessage("")
    } catch (err: any) {
      setBirthdayValidationMessage(err.message)
      validation = validation && false
    }

    try {
      await validateField(
        yup.object().shape({
          phoneNumber: yup
            .string()
            .required(t("please_input_phone_number"))
            .matches(
              /^[\+]?[(]?[0-9]{3}[)]?[-\s\.]?[0-9]{3}[-\s\.]?[0-9]{4,6}$/im,
              t("please_input_correct_phone_number")
            ),
        }),
        "phoneNumber",
        phoneNumber
      )
      setPhoneNumberValidationMessage("")
    } catch (err: any) {
      setPhoneNumberValidationMessage(err.message)
      validation = validation && false
    }

    try {
      await validateField(
        yup.object().shape({
          postCode: yup
            .string()
            .required(t("please_input_post_code"))
            .matches(/〒?[0-9]{3}-?[0-9]{4}/g, t("please_input_correct_post_code")),
        }),
        "postCode",
        postCode
      )
      setPostCodeValidationMessage("")
    } catch (err: any) {
      setPostCodeValidationMessage(err.message)
      validation = validation && false
    }

    try {
      await validateField(
        yup.object().shape({
          city: yup.string().required(t("please_input_city")),
        }),
        "city",
        city
      )
      setCityValidationMessage("")
    } catch (err: any) {
      setCityValidationMessage(err.message)
      validation = validation && false
    }

    try {
      await validateField(
        yup.object().shape({
          magazineRegister: yup.boolean().required(t("please_select_magazin_register")),
        }),
        "magazineRegister",
        magazineRegister
      )
      setMagazinValidationMessage("")
    } catch (err: any) {
      setMagazinValidationMessage(err.message)
      validation = validation && false
    }

    try {
      await validateField(
        yup.object().shape({
          startStageID: yup.number().required(t("please_select_stage")),
        }),
        "startStageID",
        startStageID
      )
      setStartStageIDValidationMessage("")
    } catch (err: any) {
      setStartStageIDValidationMessage(err.message)
      validation = validation && false
    }

    try {
      await validateField(
        yup.object().shape({
          startPlanID: yup.number().required(t("please_select_plan")),
        }),
        "startPlanID",
        startPlanID
      )
      setStartPlanIDValidationMessage("")
    } catch (err: any) {
      setStartPlanIDValidationMessage(err.message)
      validation = validation && false
    }

    try {
      if (startDate) {
        await validateField(
          yup.object().shape({
            startDate: yup
              .string()
              .matches(/^(\d{4})\-(\d{1,2})\-(\d{1,2})$/, t("please_input_correct_start_date")),
          }),
          "startDate",
          startDate.format("YYYY-MM-DD")
        )
      }
      setStartDateValidationMessage("")
    } catch (err: any) {
      setStartDateValidationMessage(err.message)
      validation = validation && false
    }

    try {
      if (siteURL) {
        await validateField(
          yup.object().shape({
            siteURL: yup
              .string()
              .matches(
                /(https?:\/\/(?:www\.|(?!www))[a-zA-Z0-9][a-zA-Z0-9-]+[a-zA-Z0-9]\.[^\s]{2,}|www\.[a-zA-Z0-9][a-zA-Z0-9-]+[a-zA-Z0-9]\.[^\s]{2,}|https?:\/\/(?:www\.|(?!www))[a-zA-Z0-9]+\.[^\s]{2,}|www\.[a-zA-Z0-9]+\.[^\s]{2,})/,
                t("please_input_correct_site_url")
              ),
          }),
          "siteURL",
          siteURL
        )
      }
      setSiteURLValidationMessage("")
    } catch (err: any) {
      setSiteURLValidationMessage(err.message)
      validation = validation && false
    }

    try {
      await validateField(
        yup.object().shape({
          route: yup.number().required(t("please_select_route")),
        }),
        "route",
        route
      )
      setRouteValidationMessage("")
    } catch (err: any) {
      setRouteValidationMessage(err.message)
      validation = validation && false
    }

    if (route === SELECT_OTHER) {
      if (!routeComment) {
        setRouteValidationMessage(t("please_input_route"))
        validation = validation && false
      }
    }

    try {
      await validateField(
        yup.object().shape({
          companyType: yup.number().required(t("please_select_company_type")),
        }),
        "companyType",
        companyType
      )
      setCompanyTypeValidationMessage("")
    } catch (err: any) {
      setCompanyTypeValidationMessage(err.message)
      validation = validation && false
    }

    if (lounge === undefined) {
      setLoungeValidationMessage(t("please_select_lounge"))
      validation = validation && false
    } else {
      setLoungeValidationMessage("")
    }

    return validation
  }

  const handleUpdate = async () => {
    if (!(await validate())) {
      return false
    }
    setShowProgress(true)
    ProfileService.updateMember(accessToken, item.userId as string, {
      firstName: firstName,
      lastName: lastName,
      firstNameKana: firstNameJP,
      lastNameKana: lastNameJP,
      gender: gender,
      birthday: birthday?.format("YYYY-MM-DD") as string,
      country: nation,
      areaId: areaId,
      phoneNumber: phoneNumber,
      zipCode: postCode,
      province: province,
      city: city,
      streetNo: street,
      building: building,
      isMagazinRegister: magazineRegister,
      startupStageId: startStageID as number,
      startupPlanId: startPlanID as number,
      startupName: companyName,
      startupKanaName: companyNameJP,
      startupOpenDate: startDate?.isValid ? startDate.format("YYYY-MM-DD") : undefined,
      startupUrl: siteURL,
      routeToKnowId: route as number,
      routeComment: routeComment,
      startupBusinessTypeId: companyType as number,
      isLoungeMember: lounge,
      comment: "USER_UPDATE",
    })
      .then((res) => {
        setShowProgress(false)
        if (!checkLoginStatus(res)) {
          return
        }

        if (res.status === RESPONSE_CODE.SUCCESS) {
          setShowToast({ show: true, text: t("success_update"), type: SNACKBAR_TYPES.SUCCESS })
        } else {
          setShowToast({ show: true, text: t("failed_update"), type: SNACKBAR_TYPES.ERROR })
        }
      })
      .catch(() => {
        setShowProgress(false)
        setShowToast({ show: true, text: t("failed_update"), type: SNACKBAR_TYPES.ERROR })
      })
  }

  const handleRemoveConfirm = () => {
    setShowProgress(true)
    ProfileService.deleteMember(accessToken, item.userId as string)
      .then((res) => {
        setShowProgress(false)
        if (!checkLoginStatus(res)) {
          return
        }

        if (res.status === RESPONSE_CODE.SUCCESS) {
          setShowToast({ show: true, text: t("success_remove"), type: SNACKBAR_TYPES.SUCCESS })
          location.reload()
        } else {
          setShowToast({ show: true, text: t("failed_remove"), type: SNACKBAR_TYPES.ERROR })
        }
      })
      .catch(() => {
        setShowProgress(false)
        setShowToast({ show: true, text: t("failed_remove"), type: SNACKBAR_TYPES.ERROR })
      })
  }

  const handleRemove = () => {
    setShowConfirm(true)
  }

  return (
    <Box sx={{ py: "40px", px: "20px" }}>
      <InputNameField
        name={t("name")}
        required
        firstName={firstName}
        lastName={lastName}
        firstNameJP={firstNameJP}
        lastNameJP={lastNameJP}
        handleFirstName={setFirstName}
        handleLastName={setLastName}
        handleFirstNameJP={setFirstNameJP}
        handleLastNameJP={setLastNameJP}
        validationMessage={nameValidationMessage}
      ></InputNameField>
      <InputDateField
        name={t("birthday")}
        required
        date={birthday}
        handleDate={setBirthday}
        validationMessage={birthdayValidationMessage}
        sx={{ mt: "30px" }}
      ></InputDateField>
      <SelectField
        name={t("sex")}
        required
        sx={{ mt: "30px", width: "200px" }}
        selected={gender}
        handleSelected={setGender}
        datas={[
          { label: t("male"), value: GENDER.MALE },
          { label: t("female"), value: GENDER.FEMALE },
        ]}
      ></SelectField>
      <SelectField
        name={t("nation")}
        required
        sx={{ mt: "30px", width: "355px" }}
        selected={nation}
        handleSelected={setNation}
        datas={getCountriesForSelect()}
      ></SelectField>
      <SelectField
        name={t("address")}
        required
        sx={{ mt: "30px", width: "200px" }}
        selected={areaId}
        handleSelected={setAreaId}
        datas={areas}
      ></SelectField>
      <InputTextField
        name={t("phone_number")}
        required
        sx={{ mt: "30px" }}
        placeholder={t("input_normal_string")}
        text={phoneNumber}
        handleText={setPhoneNumber}
        validationMessage={phoneNumberValidationMessage}
        maxLength={15}
      ></InputTextField>
      <InputTextField
        name={t("post_code")}
        required
        sx={{ mt: "30 px" }}
        placeholder={t("input_normal_string")}
        text={postCode}
        handleText={setPostCode}
        validationMessage={postCodeValidationMessage}
        handleFocusOut={handlePostCodeInput}
        maxLength={10}
      ></InputTextField>
      <SelectField
        name={t("province")}
        required
        sx={{ mt: "30px", width: "200px" }}
        selected={province}
        handleSelected={setProvince}
        datas={PROVINCES}
      ></SelectField>
      <InputTextField
        name={t("city")}
        required
        sx={{ mt: "30px" }}
        text={city}
        handleText={setCity}
        validationMessage={cityValidationMessage}
        maxLength={50}
      ></InputTextField>
      <InputTextField
        name={t("street")}
        sx={{ mt: "30px" }}
        inputStyle={{ width: "100%" }}
        text={street}
        handleText={setStreet}
        maxLength={100}
      ></InputTextField>
      <InputTextField
        name={t("building")}
        sx={{ mt: "30px" }}
        inputStyle={{ width: "100%" }}
        text={building}
        handleText={setBuilding}
        maxLength={100}
      ></InputTextField>
      <CheckBoxGroupField
        name={t("register_mail_magazine")}
        required
        radio
        datas={[
          { label: t("hope"), value: true },
          { label: t("not_hope"), value: false },
        ]}
        sx={{ mt: "30px" }}
        selected={magazineRegister}
        handleSelected={setMagazinRegister}
        validationMessage={magazinValidationMessage}
      />
      <CheckBoxGroupField
        name={t("start_step")}
        required
        radio
        sx={{ mt: "30px" }}
        datas={startupStages}
        selected={startStageID}
        handleSelected={setStartStageID}
        validationMessage={startStageIDValidationMessage}
      />
      <CheckBoxGroupField
        name={t("start_plan")}
        required
        radio
        sx={{ mt: "30px" }}
        datas={startupPlans}
        selected={startPlanID}
        handleSelected={setStartPlanID}
        validationMessage={startPlanIDValidationMessage}
      />
      <InputCompanyField
        name={t("company_information")}
        companyName={companyName}
        handleCompanyName={setCompanyName}
        companyNameJP={companyNameJP}
        handleCompanyNameJP={setCompanyNameJP}
        sx={{ mt: "30px" }}
      />
      <InputDateField
        name={t("start_company_date")}
        sx={{ mt: "30px" }}
        date={startDate}
        handleDate={setStartDate}
        validationMessage={startDateValidationMessage}
      />
      <InputTextField
        name={t("site_url")}
        sx={{ mt: "30px" }}
        inputStyle={{ width: "355px" }}
        text={siteURL}
        handleText={setSiteURL}
        validationMessage={siteURLValidationMessage}
        maxLength={200}
      />
      <CheckBoxGroupField
        required
        radio
        hasText
        name={t("known_route")}
        datas={routes}
        selected={route}
        handleSelected={setRoute}
        text={routeComment}
        handleText={setRouteComment}
        validationMessage={routeValidationMessage}
        multiline
        sx={{ mt: "30px" }}
      />
      <CheckBoxGroupField
        required
        radio
        name={t("company_type")}
        datas={companyTypes}
        selected={companyType}
        handleSelected={setCompanyType}
        validationMessage={companyTypeValidationMessage}
        sx={{ mt: "30px" }}
      />
      <CheckBoxGroupField
        required
        radio
        name={t("is_lounge")}
        datas={[
          {
            label: t("lounge"),
            value: true,
          },
          {
            label: t("not_lounge"),
            value: false,
          },
        ]}
        selected={lounge}
        handleSelected={setLounge}
        sx={{ mt: "30px" }}
        validationMessage={loungeValidationMessage}
      />
      <Box display="flex" sx={{ mt: "30px" }}>
        <OutlinedButton
          caption={t("delete")}
          fontColor="#FC3317"
          fontWeight="700"
          fontSize={16}
          sx={{
            borderColor: "#FC3317",
            minWidth: "240px",
            py: "16px",
            fontSize: "1rem",
            fontFamily: "Noto Sans JP !important",
            lineHeight: "1rem",
            fontWeight: 700,
            ml: "auto",
            mr: "40px",
            display: "flex",
            justifyContent: "center",
            borderRadius: 0,
          }}
          onClick={() => handleRemove()}
        />
        <Button
          variant="contained"
          sx={{
            minWidth: "240px",
            py: "16px",
            fontSize: "1rem",
            fontFamily: "Noto Sans JP !important",
            lineHeight: "1rem",
            fontWeight: 700,
            ml: 0,
            mr: "auto",
            display: "flex",
            justifyContent: "center",
            borderRadius: 0,
            ":hover": {
              bgcolor: "#2DA389",
            },
          }}
          onClick={() => handleUpdate()}
        >
          {t("update")}
        </Button>
      </Box>
      <ConfirmModal
        open={showConfirm}
        handleOpen={setShowConfirm}
        text={t("do_you_want_to_remove")}
        confirmText={t("remove")}
        cancelText={t("cancel")}
        handleConfirm={handleRemoveConfirm}
        confirmStyle={{ color: "#FC3317", borderColor: "#FC3317" }}
      />
    </Box>
  )
}
